/**
 * useConversationNotes Hook - Phase 6A: Internal Agent Communication
 * 
 * Custom React hook for managing conversation notes with state management,
 * caching, optimistic updates, and error handling following existing patterns.
 */

import { useState, useCallback, useEffect } from 'react'
import { conversationNotesService } from '../services/conversationNotes'
import type { GetNotesOptions } from '../services/conversationNotes'
import { logger } from '../utils/logger'
import type {
  ConversationNote,
  CreateConversationNoteRequest,
  UpdateConversationNoteRequest,
  ConversationNotesListResponse
} from '../types'

export interface UseConversationNotesOptions {
  conversationId: string
  autoFetch?: boolean
  pollInterval?: number
  onError?: (error: string) => void
  onNoteCreated?: (note: ConversationNote) => void
  onNoteUpdated?: (note: ConversationNote) => void
  onNoteDeleted?: (noteId: string) => void
}

export interface UseConversationNotesReturn {
  // State
  notes: ConversationNote[]
  isLoading: boolean
  isCreating: boolean
  error: string | null
  hasMore: boolean
  totalCount: number
  
  // Actions
  fetchNotes: (options?: GetNotesOptions) => Promise<void>
  createNote: (noteData: Omit<CreateConversationNoteRequest, 'conversation_id'>) => Promise<ConversationNote | null>
  updateNote: (noteId: string, updateData: UpdateConversationNoteRequest) => Promise<ConversationNote | null>
  deleteNote: (noteId: string) => Promise<boolean>
  refreshNotes: () => Promise<void>
  
  // Utilities
  clearError: () => void
  getNote: (noteId: string) => ConversationNote | undefined
  getNotesCountByType: (noteType: string) => number
  getPinnedNotes: () => ConversationNote[]
}

export function useConversationNotes(options: UseConversationNotesOptions): UseConversationNotesReturn {
  const {
    conversationId,
    autoFetch = true,
    pollInterval,
    onError,
    onNoteCreated,
    onNoteUpdated,
    onNoteDeleted
  } = options

  // State management
  const [notes, setNotes] = useState<ConversationNote[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Fetch conversation notes with optional filtering
   */
  const fetchNotes = useCallback(async (fetchOptions: GetNotesOptions = {}): Promise<void> => {
    if (!conversationId) {
      logger.warn('Cannot fetch notes: conversationId is required')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      logger.info('Fetching conversation notes', {
        conversationId,
        options: fetchOptions,
        action: 'fetch_notes'
      })

      const response: ConversationNotesListResponse = await conversationNotesService.getNotes(
        conversationId,
        fetchOptions
      )

      // Sort notes: pinned first, then by creation date (newest first)
      const sortedNotes = response.notes.sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1
        if (!a.is_pinned && b.is_pinned) return 1
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })

      setNotes(sortedNotes)
      setHasMore(response.has_more)
      setTotalCount(response.total_count)

      logger.info('Successfully fetched conversation notes', {
        conversationId,
        notesCount: response.notes.length,
        totalCount: response.total_count,
        hasMore: response.has_more,
        action: 'fetch_notes_success'
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch conversation notes'
      logger.error('Failed to fetch conversation notes', {
        conversationId,
        error: errorMessage,
        action: 'fetch_notes_error'
      })

      setError(errorMessage)
      onError?.(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [conversationId, onError])

  /**
   * Create a new conversation note with optimistic update
   */
  const createNote = useCallback(async (
    noteData: Omit<CreateConversationNoteRequest, 'conversation_id'>
  ): Promise<ConversationNote | null> => {
    if (!conversationId) {
      const error = 'Cannot create note: conversationId is required'
      setError(error)
      return null
    }

    setIsCreating(true)
    setError(null)

    try {
      logger.info('Creating conversation note', {
        conversationId,
        noteType: noteData.note_type,
        priority: noteData.priority,
        contentLength: noteData.content.length,
        action: 'create_note'
      })

      const newNote = await conversationNotesService.createNote({
        ...noteData,
        conversation_id: conversationId
      })

      // Optimistic update: add note to the list
      setNotes(prevNotes => {
        const updatedNotes = [newNote, ...prevNotes]
        // Re-sort to maintain pinned notes at top
        return updatedNotes.sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1
          if (!a.is_pinned && b.is_pinned) return 1
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        })
      })

      setTotalCount(prev => prev + 1)

      logger.info('Successfully created conversation note', {
        noteId: newNote.id,
        conversationId,
        action: 'create_note_success'
      })

      onNoteCreated?.(newNote)
      return newNote
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create conversation note'
      logger.error('Failed to create conversation note', {
        conversationId,
        error: errorMessage,
        action: 'create_note_error'
      })

      setError(errorMessage)
      onError?.(errorMessage)
      return null
    } finally {
      setIsCreating(false)
    }
  }, [conversationId, onError, onNoteCreated])

  /**
   * Update an existing conversation note with optimistic update
   */
  const updateNote = useCallback(async (
    noteId: string,
    updateData: UpdateConversationNoteRequest
  ): Promise<ConversationNote | null> => {
    setError(null)

    // Optimistic update: apply changes immediately
    const originalNotes = notes
    setNotes(prevNotes =>
      prevNotes.map(note =>
        note.id === noteId
          ? { ...note, ...updateData, is_edited: true, updated_at: new Date().toISOString() }
          : note
      )
    )

    try {
      logger.info('Updating conversation note', {
        noteId,
        updateFields: Object.keys(updateData),
        action: 'update_note'
      })

      const updatedNote = await conversationNotesService.updateNote(noteId, updateData)

      // Update with actual server response
      setNotes(prevNotes =>
        prevNotes.map(note => note.id === noteId ? updatedNote : note)
      )

      logger.info('Successfully updated conversation note', {
        noteId,
        action: 'update_note_success'
      })

      onNoteUpdated?.(updatedNote)
      return updatedNote
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update conversation note'
      logger.error('Failed to update conversation note', {
        noteId,
        error: errorMessage,
        action: 'update_note_error'
      })

      // Revert optimistic update on error
      setNotes(originalNotes)
      setError(errorMessage)
      onError?.(errorMessage)
      return null
    }
  }, [notes, onError, onNoteUpdated])

  /**
   * Delete a conversation note with optimistic update
   */
  const deleteNote = useCallback(async (noteId: string): Promise<boolean> => {
    setError(null)

    // Optimistic update: remove note immediately
    const noteToDelete = notes.find(note => note.id === noteId)
    const originalNotes = notes
    setNotes(prevNotes => prevNotes.filter(note => note.id !== noteId))
    setTotalCount(prev => prev - 1)

    try {
      logger.info('Deleting conversation note', {
        noteId,
        action: 'delete_note'
      })

      await conversationNotesService.deleteNote(noteId)

      logger.info('Successfully deleted conversation note', {
        noteId,
        action: 'delete_note_success'
      })

      onNoteDeleted?.(noteId)
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete conversation note'
      logger.error('Failed to delete conversation note', {
        noteId,
        error: errorMessage,
        action: 'delete_note_error'
      })

      // Revert optimistic update on error
      setNotes(originalNotes)
      setTotalCount(prev => prev + 1)
      setError(errorMessage)
      onError?.(errorMessage)
      return false
    }
  }, [notes, onError, onNoteDeleted])

  /**
   * Refresh notes (alias for fetchNotes)
   */
  const refreshNotes = useCallback(() => fetchNotes(), [fetchNotes])

  /**
   * Get a specific note by ID
   */
  const getNote = useCallback((noteId: string): ConversationNote | undefined => {
    return notes.find(note => note.id === noteId)
  }, [notes])

  /**
   * Get count of notes by type
   */
  const getNotesCountByType = useCallback((noteType: string): number => {
    return notes.filter(note => note.note_type === noteType).length
  }, [notes])

  /**
   * Get all pinned notes
   */
  const getPinnedNotes = useCallback((): ConversationNote[] => {
    return notes.filter(note => note.is_pinned)
  }, [notes])

  // Auto-fetch notes on mount or when conversationId changes
  useEffect(() => {
    if (autoFetch && conversationId) {
      fetchNotes()
    }
  }, [autoFetch, conversationId, fetchNotes])

  // Polling interval for real-time updates
  useEffect(() => {
    if (pollInterval && conversationId) {
      const interval = setInterval(() => {
        fetchNotes()
      }, pollInterval)

      return () => clearInterval(interval)
    }
  }, [pollInterval, conversationId, fetchNotes])

  return {
    // State
    notes,
    isLoading,
    isCreating,
    error,
    hasMore,
    totalCount,
    
    // Actions
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
    refreshNotes,
    
    // Utilities
    clearError,
    getNote,
    getNotesCountByType,
    getPinnedNotes
  }
}